import { BaseService } from './api/BaseService';
import { API_ENDPOINTS } from './api/config';
import api from './api';
import { apiClient } from './apiClient';
import { UUID } from '../types/common';
import {
  PrimeArtifact,
  PrimeArtifactWithParameters,
  UpdatePrimeArtifact,
  StateDependency,
  PrimeCreationRelationship,
  CreateCreationRelationship,
  UpdateCreationRelationship,
} from '../types/primeArtifact';
import { ArtifactType } from '../types/artifactTypes';
import {
  PrimeArtifactState,
  PrimeArtifactStateTransition,
  PrimeArtifactStateTransitionCreate,
} from '../types/primeModel';
import { PrimeArtifactStateCreate } from '../types/primeArtifactState';
import { PrimeArtifactStateTransitionUpdate } from '../types/primeArtifactStateTransition';
import { PrimeProcess } from '../types/primeModel';
import { artifactTypeService } from './artifactTypeService';
import { StateCentricState } from '../types/primeModelViews';

export class PrimeArtifactService extends BaseService {
  static async getPrimeArtifacts(): Promise<PrimeArtifact[]> {
    return this.getAll();
  }

  static async getAll(): Promise<PrimeArtifact[]> {
    const allArtifacts: PrimeArtifact[] = [];
    let skip = 0;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await api.get<PrimeArtifact[]>(
        `${API_ENDPOINTS.PRIME_ARTIFACTS.BASE}?skip=${skip}&limit=${limit}`
      );
      const artifacts = response.data;
      allArtifacts.push(...artifacts);
      // If we got fewer artifacts than the limit, we've reached the end
      if (artifacts.length < limit) {
        hasMore = false;
      } else {
        skip += limit;
      }
    }

    return allArtifacts;
  }

  static async getById(id: string): Promise<PrimeArtifact> {
    return this.get<PrimeArtifact>(API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(id));
  }

  static async create(data: Partial<PrimeArtifact>): Promise<PrimeArtifact> {
    return this.post<PrimeArtifact>(API_ENDPOINTS.PRIME_ARTIFACTS.BASE, data);
  }

  static async update(id: string, data: Partial<PrimeArtifact>): Promise<PrimeArtifact> {
    return this.put<PrimeArtifact>(API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(id), data);
  }

  static async delete(id: string): Promise<void> {
    return super.delete(API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(id));
  }

  static async search(name: string): Promise<PrimeArtifact[]> {
    return this.get<PrimeArtifact[]>(API_ENDPOINTS.PRIME_ARTIFACTS.SEARCH(name));
  }

  static async getArtifactsHierarchy(): Promise<PrimeArtifact[]> {
    return this.get<PrimeArtifact[]>(API_ENDPOINTS.PRIME_ARTIFACTS.HIERARCHY);
  }

  static async updateArtifact(id: string, artifact: UpdatePrimeArtifact): Promise<PrimeArtifact> {
    return this.put<PrimeArtifact>(API_ENDPOINTS.PRIME_ARTIFACTS.UPDATE_ARTIFACT(id), artifact);
  }

  static async getParameters(artifactId: string) {
    return this.get<Record<string, unknown>>(API_ENDPOINTS.PRIME_ARTIFACTS.PARAMETERS(artifactId));
  }

  static async getParameterDependencies(artifactId: string): Promise<StateDependency[]> {
    return this.get<StateDependency[]>(API_ENDPOINTS.PRIME_ARTIFACTS.DEPENDENCIES(artifactId));
  }

  static async getPrimeArtifactWithParameters(id: string): Promise<PrimeArtifactWithParameters> {
    return this.get<PrimeArtifactWithParameters>(API_ENDPOINTS.PRIME_ARTIFACTS.WITH_PARAMETERS(id));
  }

  static async getRootArtifacts(): Promise<PrimeArtifact[]> {
    return this.get<PrimeArtifact[]>(API_ENDPOINTS.PRIME_ARTIFACTS.ROOTS);
  }

  static async getWorkflowList(): Promise<{
    artifacts: import('../types/primeArtifact').PrimeArtifactWorkflowItem[];
    total: number;
    statistics: import('../types/primeArtifact').WorkflowStatistics;
  }> {
    // Use /workflow-status endpoint directly (ISSUE-114: backend now has alias, but we use the primary endpoint)
    return await this.getWorkflowListFromStatus();
  }

  /**
   * Get workflow list from /workflow-status endpoint
   * Transforms the response format to match expected structure
   */
  private static async getWorkflowListFromStatus(): Promise<{
    artifacts: import('../types/primeArtifact').PrimeArtifactWorkflowItem[];
    total: number;
    statistics: import('../types/primeArtifact').WorkflowStatistics;
  }> {
    const response = await this.get<{
      metrics: {
        IDENTIFIED: number;
        DEFINED: number;
        DESIGNED: number;
        IMPLEMENTED: number;
        VALIDATED: number;
        TOTAL: number;
      };
      artifacts_by_state: {
        IDENTIFIED: Array<{
          id: string;
          name: string;
          description?: string;
          artifact_type_id?: string;
          development_status: string;
          is_template_ready?: boolean;
          created_at?: string;
          updated_at?: string;
        }>;
        DEFINED: Array<{
          id: string;
          name: string;
          description?: string;
          artifact_type_id?: string;
          development_status: string;
          is_template_ready?: boolean;
          created_at?: string;
          updated_at?: string;
        }>;
        DESIGNED: Array<{
          id: string;
          name: string;
          description?: string;
          artifact_type_id?: string;
          development_status: string;
          is_template_ready?: boolean;
          created_at?: string;
          updated_at?: string;
        }>;
        IMPLEMENTED: Array<{
          id: string;
          name: string;
          description?: string;
          artifact_type_id?: string;
          development_status: string;
          is_template_ready?: boolean;
          created_at?: string;
          updated_at?: string;
        }>;
        VALIDATED: Array<{
          id: string;
          name: string;
          description?: string;
          artifact_type_id?: string;
          development_status: string;
          is_template_ready?: boolean;
          created_at?: string;
          updated_at?: string;
        }>;
      };
      workflow_states: string[];
    }>(`${API_ENDPOINTS.PRIME_ARTIFACTS.BASE}/workflow-status`);

    // Transform response to expected format
    // Flatten artifacts from all states into a single list
    const allArtifacts: import('../types/primeArtifact').PrimeArtifactWorkflowItem[] = [];
    
    // /workflow-status now includes counts (enhanced in backend)
    for (const state of response.workflow_states) {
      const artifacts = response.artifacts_by_state[state as keyof typeof response.artifacts_by_state] || [];
      for (const artifact of artifacts) {
        allArtifacts.push({
          id: artifact.id,
          name: artifact.name,
          description: artifact.description,
          artifact_type_name: (artifact as any).artifact_type_name || null,
          state_template_name: null,
          properties_count: (artifact as any).properties_count || 0,
          states_count: (artifact as any).states_count || 0,
          transitions_count: (artifact as any).transitions_count || 0,
          development_status: artifact.development_status as any,
        });
      }
    }

    return {
      artifacts: allArtifacts,
      total: response.metrics.TOTAL,
      statistics: {
        total_artifacts: response.metrics.TOTAL,
        by_status: {
          IDENTIFIED: response.metrics.IDENTIFIED,
          DEFINED: response.metrics.DEFINED,
          DESIGNED: response.metrics.DESIGNED || 0,
          IMPLEMENTED: response.metrics.IMPLEMENTED,
          VALIDATED: response.metrics.VALIDATED,
        },
      },
    };
  }

  static async getPrimeArtifactChildren(primeArtifactId: string): Promise<PrimeArtifact[]> {
    try {
      // Use the dependencies endpoint which returns children along with other data
      // Construct the full path - API_ENDPOINTS returns path without /api/v1 prefix
      const endpointPath = API_ENDPOINTS.PRIME_ARTIFACTS.DEPENDENCIES(primeArtifactId);
      const response = await apiClient.get<{
        artifact_id: string;
        artifact_name: string;
        parent?: {
          id: string;
          name: string;
          description?: string;
          artifact_type_id: string;
        };
        children: Array<{
          id: string;
          name: string;
          description?: string;
          artifact_type_id: string;
        }>;
        processes?: any[];
        relationships?: any[];
        similar_artifacts?: any[];
      }>(endpointPath);
      
      // Extract and map children to PrimeArtifact format
      const children = response.data.children || [];
      return children.map((child) => ({
        id: child.id,
        name: child.name,
        description: child.description,
        artifact_type_id: child.artifact_type_id,
      } as PrimeArtifact));
    } catch (error) {
      console.error('Error fetching prime artifact children:', error);
      throw error;
    }
  }

  static async getPrimeArtifactPeers(primeArtifactId: string): Promise<PrimeArtifact[]> {
    try {
      // First, get the prime artifact to find its parent
      const primeArtifact = await this.getById(primeArtifactId);
      
      if (!primeArtifact.parent_ref) {
        // If no parent, return empty array (root artifacts have no peers)
        return [];
      }

      // Get all children of the parent (peers) using dependencies endpoint
      const response = await apiClient.get<{
        artifact_id: string;
        artifact_name: string;
        parent?: {
          id: string;
          name: string;
          description?: string;
          artifact_type_id: string;
        };
        children: Array<{
          id: string;
          name: string;
          description?: string;
          artifact_type_id: string;
        }>;
        processes?: any[];
        relationships?: any[];
        similar_artifacts?: any[];
      }>(API_ENDPOINTS.PRIME_ARTIFACTS.DEPENDENCIES(primeArtifact.parent_ref));
      
      // Extract children and filter out the selected artifact itself
      const allSiblings = response.data.children || [];
      return allSiblings
        .filter((child) => child.id !== primeArtifactId)
        .map((child) => ({
          id: child.id,
          name: child.name,
          description: child.description,
          artifact_type_id: child.artifact_type_id,
        } as PrimeArtifact));
    } catch (error) {
      console.error('Error fetching prime artifact peers:', error);
      throw error;
    }
  }

  static async getPrimeArtifactStates(prime_artifact_id: string): Promise<PrimeArtifactState[]> {
    return this.get<PrimeArtifactState[]>(API_ENDPOINTS.PRIME_ARTIFACTS.STATES(prime_artifact_id));
  }

  static async createPrimeArtifactState(
    state: PrimeArtifactStateCreate | any
  ): Promise<PrimeArtifactState> {
    // Backend expects prime_artifact_id in the request body, but we use it from URL path
    // Extract prime_artifact_id (or prime_artifact_ref) for the URL
    const artifactId = (state as any).prime_artifact_id || (state as any).prime_artifact_ref;
    if (!artifactId) {
      throw new Error('prime_artifact_id or prime_artifact_ref is required');
    }
    
    // Create a copy of state data for the request body
    const requestData = { ...state } as Record<string, unknown>;
    
    // Ensure prime_artifact_id is in the body (backend requires it)
    if (!requestData.prime_artifact_id && artifactId) {
      requestData.prime_artifact_id = artifactId;
    }
    
    return this.post<PrimeArtifactState>(
      API_ENDPOINTS.PRIME_ARTIFACTS.STATES(artifactId),
      requestData
    );
  }

  static async updatePrimeArtifactState(
    stateId: string,
    state: Partial<PrimeArtifactState>
  ): Promise<PrimeArtifactState> {
    const artifactId = state.prime_artifact_ref;
    if (!artifactId) {
      throw new Error('Cannot update state: prime_artifact_ref is missing');
    }
    return this.put<PrimeArtifactState>(
      API_ENDPOINTS.PRIME_ARTIFACTS.UPDATE_STATE(artifactId, stateId),
      state
    );
  }

  static async deletePrimeArtifactState(artifactId: string, stateId: string): Promise<void> {
    try {
      const response = await api.delete(
        API_ENDPOINTS.PRIME_ARTIFACTS.DELETE_STATE(artifactId, stateId)
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting state:', error);
      throw error;
    }
  }

  static async getPrimeArtifactTransitions(
    prime_artifact_id: string,
    is_improvement?: boolean
  ): Promise<PrimeArtifactStateTransition[]> {
    const params: Record<string, string> = {};
    if (is_improvement !== undefined) {
      params.is_improvement = String(is_improvement);
    }
    const queryString = new URLSearchParams(params).toString();
    const url = queryString 
      ? `${API_ENDPOINTS.PRIME_ARTIFACT_TRANSITIONS.BY_ARTIFACT(prime_artifact_id)}?${queryString}`
      : API_ENDPOINTS.PRIME_ARTIFACT_TRANSITIONS.BY_ARTIFACT(prime_artifact_id);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[PrimeArtifactService] Fetching prime transitions from:', url);
    }
    
    const transitions = await this.get<PrimeArtifactStateTransition[]>(url);
    
    if (process.env.NODE_ENV === 'development' && transitions.length > 0) {
      console.log('[PrimeArtifactService] Received prime transitions:', {
        count: transitions.length,
        sample: transitions[0] ? {
          ...transitions[0],
          keys: Object.keys(transitions[0]),
        } : null,
        sample_from_state_ref: transitions[0]?.from_state_ref,
        sample_to_state_ref: transitions[0]?.to_state_ref,
        sample_from_state_id: transitions[0] && 'from_state_id' in transitions[0] ? (transitions[0] as any).from_state_id : undefined,
        sample_to_state_id: transitions[0] && 'to_state_id' in transitions[0] ? (transitions[0] as any).to_state_id : undefined,
      });
    }
    
    return transitions;
  }

  static async getPrimeArtifactProcesses(prime_artifact_id: string): Promise<PrimeProcess[]> {
    return this.get<PrimeProcess[]>(API_ENDPOINTS.PRIME_PROCESSES.BY_ARTIFACT(prime_artifact_id));
  }

  static async createPrimeArtifactTransition(
    artifact_id: string,
    transition: PrimeArtifactStateTransitionCreate
  ): Promise<PrimeArtifactStateTransition> {
    return this.post<PrimeArtifactStateTransition>(
      API_ENDPOINTS.PRIME_ARTIFACTS.TRANSITIONS(artifact_id),
      transition
    );
  }

  static async updatePrimeArtifactTransition(
    artifact_id: string,
    transition_id: string,
    transition: PrimeArtifactStateTransitionUpdate
  ): Promise<PrimeArtifactStateTransition> {
    return this.put<PrimeArtifactStateTransition>(
      API_ENDPOINTS.PRIME_ARTIFACTS.UPDATE_TRANSITION(artifact_id, transition_id),
      transition as Record<string, unknown>
    );
  }

  static async deletePrimeArtifactTransition(
    artifact_id: string,
    transition_id: string
  ): Promise<void> {
    return this.delete(API_ENDPOINTS.PRIME_ARTIFACTS.DELETE_TRANSITION(artifact_id, transition_id));
  }

  static async getAvailableParents(currentArtifactId: string): Promise<PrimeArtifact[]> {
    const response = await api.get(`/prime-artifacts/available-parents/${currentArtifactId}`);
    return response.data;
  }

  static async getStateCentricView(
    artifactId: string,
    stateId: string
  ): Promise<StateCentricState> {
    try {
      const response = await this.get<StateCentricState>(
        API_ENDPOINTS.PRIME_ARTIFACTS.STATE_VIEW(artifactId, stateId)
      );

      // Validate the response
      if (!response.id || !response.name || !response.prime_artifact_ref) {
        throw new Error('Invalid state-centric view response');
      }

      return response;
    } catch (error) {
      console.error('Error fetching state-centric view:', error);
      throw error;
    }
  }

  static async getTabValidation(artifactId: string): Promise<any> {
    const response = await api.get(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/tab-validation'
    );
    return response.data;
  }

  static async approveDetails(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/approve-details'
    );
    return response.data;
  }

  static async approveStates(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/approve-states'
    );
    return response.data;
  }

  static async approveTransitions(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/approve-transitions'
    );
    return response.data;
  }

  static async approveProcesses(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/approve-processes'
    );
    return response.data;
  }

  static async rejectDetails(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/reject-details'
    );
    return response.data;
  }

  static async rejectStates(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/reject-states'
    );
    return response.data;
  }

  static async rejectTransitions(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/reject-transitions'
    );
    return response.data;
  }

  static async rejectProcesses(artifactId: string): Promise<any> {
    const response = await api.put(
      API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId) + '/reject-processes'
    );
    return response.data;
  }

  // Artifact Parameter Management
  static async getArtifactParameters(artifactId: string): Promise<any[]> {
    // Use the endpoint from prime_artifact_parameters.py: /prime-artifact-parameters/artifact/{artifact_id}
    const response = await apiClient.get(
      `/prime-artifact-parameters/artifact/${artifactId}`
    );
    return response.data;
  }

  static async addSkillParameter(
    artifactId: string, 
    skillParameterId: string,
    parameterTypeId: string
  ): Promise<any> {
    // Use the endpoint from prime_artifacts.py: POST /prime-artifacts/{artifact_id}/parameters (no trailing slash)
    const response = await apiClient.post(
      `/prime-artifacts/${artifactId}/parameters`,
      {
        name: 'Skill Parameter',  // Will be updated with skill name
        parameter_type_id: parameterTypeId,
        prime_artifact_id: artifactId,
        is_required: false
      }
    );
    
    // Create skill relationship
    const parameterId = response.data.id;
    await apiClient.post('/skills/parameter-relationships', {
      skill_parameter_id: skillParameterId,
      prime_parameter_ref: parameterId
    });
    
    return response.data;
  }

  static async removeParameter(artifactId: string, parameterId: string): Promise<void> {
    // Use the endpoint from prime_artifacts.py: DELETE /prime-artifacts/{artifact_id}/parameters/{parameter_id} (no trailing slash)
    await apiClient.delete(
      `/prime-artifacts/${artifactId}/parameters/${parameterId}`
    );
  }

  static async updateParameter(
    artifactId: string,
    parameterId: string,
    data: any
  ): Promise<any> {
    // Use the endpoint from prime_artifacts.py: PUT /prime-artifacts/{artifact_id}/parameters/{parameter_id} (no trailing slash)
    const response = await apiClient.put(
      `/prime-artifacts/${artifactId}/parameters/${parameterId}`,
      data
    );
    return response.data;
  }

  // ============================================================================
  // Creation Relationships (ISSUE-154)
  // ============================================================================

  /**
   * Get all creation relationships for a prime artifact
   * Returns relationships where this artifact is the creator
   */
  static async getCreationRelationships(artifactId: string): Promise<PrimeCreationRelationship[]> {
    console.log('[PrimeArtifactService] Fetching creation relationships:', {
      artifactId,
      endpoint: `/prime-artifacts/${artifactId}/creation-relationships`
    });
    
    const response = await apiClient.get(
      `/prime-artifacts/${artifactId}/creation-relationships`
    );
    
    console.log('[PrimeArtifactService] Received creation relationships:', {
      status: response.status,
      data: response.data,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      count: Array.isArray(response.data) ? response.data.length : 'N/A',
      dataKeys: typeof response.data === 'object' && response.data !== null ? Object.keys(response.data) : 'N/A',
      dataJSON: JSON.stringify(response.data, null, 2)
    });
    
    // Handle both array response and object-wrapped response
    // Backend returns: { as_creator: [...], as_created: [...] }
    let relationships: PrimeCreationRelationship[];
    if (Array.isArray(response.data)) {
      relationships = response.data;
    } else if (response.data && typeof response.data === 'object') {
      // Backend returns object with as_creator and as_created arrays
      if (Array.isArray(response.data.as_creator)) {
        relationships = response.data.as_creator;
      } else if (Array.isArray(response.data.relationships)) {
        relationships = response.data.relationships;
      } else if (Array.isArray(response.data.data)) {
        relationships = response.data.data;
      } else if (Array.isArray(response.data.items)) {
        relationships = response.data.items;
      } else {
        console.warn('[PrimeArtifactService] Unexpected response format for creation relationships:', response.data);
        relationships = [];
      }
    } else {
      relationships = [];
    }
    
    console.log('[PrimeArtifactService] Extracted relationships:', {
      count: relationships.length,
      relationships
    });
    
    return relationships;
  }

  /**
   * Create a new creation relationship
   * Defines that this artifact can create the target artifact
   */
  static async createCreationRelationship(
    artifactId: string,
    data: CreateCreationRelationship
  ): Promise<PrimeCreationRelationship> {
    console.log('[PrimeArtifactService] Creating creation relationship:', {
      artifactId,
      endpoint: `/prime-artifacts/${artifactId}/creation-relationships`,
      payload: data,
      payloadJSON: JSON.stringify(data, null, 2)
    });
    
    const response = await apiClient.post(
      `/prime-artifacts/${artifactId}/creation-relationships`,
      data
    );
    
    console.log('[PrimeArtifactService] Creation relationship created successfully:', response.data);
    return response.data;
  }

  /**
   * Get a specific creation relationship by ID
   */
  static async getCreationRelationship(
    artifactId: string,
    relationshipId: string
  ): Promise<PrimeCreationRelationship> {
    const response = await apiClient.get(
      `/prime-artifacts/${artifactId}/creation-relationships/${relationshipId}`
    );
    return response.data;
  }

  /**
   * Update an existing creation relationship
   */
  static async updateCreationRelationship(
    artifactId: string,
    relationshipId: string,
    data: UpdateCreationRelationship
  ): Promise<PrimeCreationRelationship> {
    const response = await apiClient.put(
      `/prime-artifacts/${artifactId}/creation-relationships/${relationshipId}`,
      data
    );
    return response.data;
  }

  /**
   * Delete a creation relationship
   */
  static async deleteCreationRelationship(
    artifactId: string,
    relationshipId: string
  ): Promise<void> {
    await apiClient.delete(
      `/prime-artifacts/${artifactId}/creation-relationships/${relationshipId}`
    );
  }

  /**
   * Get list of artifacts that this artifact can create
   */
  static async getCanCreate(artifactId: string): Promise<PrimeArtifact[]> {
    const response = await apiClient.get(
      `/prime-artifacts/${artifactId}/can-create`
    );
    return response.data;
  }

  /**
   * Get list of artifacts that can create this artifact
   */
  static async getCreatedBy(artifactId: string): Promise<PrimeArtifact[]> {
    console.log('[PrimeArtifactService] Fetching created-by artifacts:', {
      artifactId,
      endpoint: `/prime-artifacts/${artifactId}/created-by`
    });
    
    const response = await apiClient.get(
      `/prime-artifacts/${artifactId}/created-by`
    );
    
    console.log('[PrimeArtifactService] Received created-by artifacts:', {
      status: response.status,
      data: response.data,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      count: Array.isArray(response.data) ? response.data.length : 'N/A',
      sample: Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : 'N/A',
      dataJSON: JSON.stringify(response.data, null, 2)
    });
    
    return response.data;
  }
}

export const {
  getAll,
  create,
  update,
  delete: deleteArtifact,
  getById,
  getArtifactsHierarchy,
  updateArtifact,
  getParameters,
  getParameterDependencies,
  getPrimeArtifactWithParameters,
  getRootArtifacts,
  getTabValidation,
  approveDetails,
  approveStates,
  approveTransitions,
  approveProcesses,
  rejectDetails,
  rejectStates,
  rejectTransitions,
  rejectProcesses,
} = PrimeArtifactService;

export const getArtifactTypes = async (): Promise<ArtifactType[]> => {
  return artifactTypeService.getAll();
};

export const getPrimeArtifacts = async (): Promise<PrimeArtifact[]> => {
  return PrimeArtifactService.getAll();
};
